import { useState, useEffect, useMemo } from "react";

import { ThemeProvider } from "@mui/material/styles";
// eslint-disable-next-line import/order
import Box from "@mui/material/Box";
import { QueryClient, QueryClientProvider } from "react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";

import Account from "@/account/Account";
import Actuals from "@/actuals/Actuals";
import AccountingBills from "@/accounting_bills/AccountingBills";
import Admin from "@/admin/Admin";
import SuperAdmin from "@/admin/SuperAdmin";
import TidioChatController from "@/ai/TidioChatController";
import CanvasPage from "@/ai/CanvasPage";
import Api from "@/Api";
import _404 from "@/auth/404";
import Auth from "@/auth/Auth";
import {
    PERMISSION_GET_PROJECTS,
    PERMISSION_GET_BIDS,
    PERMISSION_POST_BIDS,
    PERMISSION_GET_INVOICES,
    PERMISSION_GET_BILLS,
    PERMISSION_EDIT_PERSONS,
    PERMISSION_GET_CLIENTS,
    PERMISSION_EDIT_CLIENTS,
    PERMISSION_VIEW_ADMIN_PANEL,
} from "@/auth/constants";
import ForgotPassword from "@/auth/ForgotPassword";
import Init from "@/auth/Init";
import Login from "@/auth/Login";
import Logout from "@/auth/Logout";
import JoinOrganization from "@/auth/OrganizationJoiner";
import ProtectedComponents from "@/auth/ProtectedComponents";
import ProtectedContainer from "@/auth/ProtectedContainer";
import ResetPassword from "@/auth/ResetPassword";
import SetUp from "@/auth/SetUp";
import SignUp from "@/auth/SignUp";
import Verify from "@/auth/Verify";
import Bids from "@/bids/Bids";
import TemplateBids from "@/bids/TemplateBids";
import Bills from "@/bills/Bills";
import Client from "@/clients/Client";
import Clients from "@/clients/Clients";
import BaseContainer from "@/common/BaseContainer";
import ScrollToTop from "@/common/ScrollToTop";
import {
    FEATURE_BIDS,
    FEATURE_INVOICES,
    FEATURE_MULTI_PERSON,
} from "@/constants";
import Dashboard from "@/dashboard/Dashboard";
import Invoices from "@/invoices/Invoices";
import Error from "@/nav/Error";
import Footer from "@/nav/Footer";
import MobileNav from "@/nav/MobileNav";
import Popups from "@/nav/Popups";
import PublicNav from "@/nav/PublicNav";
import SideNav from "@/nav/SideNav";
import TopNav from "@/nav/TopNav";
import { OrgProfile } from "@/org_profile/OrgProfile";
import PaymentFailed from "@/payment/PaymentFailed";
import PaymentRequired from "@/payment/PaymentRequired";
import PaymentSuccess from "@/payment/PaymentSuccess";
import { PersonProfile } from "@/person_profile/PersonProfile";
import PersonProfiles from "@/person_profiles/PersonProfiles";
import Persons from "@/persons/Persons";
import Project from "@/project/Project";
import { ProjectProfile } from "@/project_profiles/ProjectProfile";
import Projects from "@/projects/Projects";
import Confirmation from "@/public/Confirmation";
import PublicTerm from "@/public/PublicTerm";
import Reports from "@/report/Reports";
import BidSheet from "@/sheet/BidSheet";
import { GlobalStyle, baseTheme, darkTheme } from "@/themes";
import Vendors from "@/vendor/Vendors";
import InvoicePortal from "@/vendor/InvoicePortal";
import InvoicePortalVendorPage from "@/vendor/InvoicePortalVendorPage";
import InvoicePortalInvoicePage from "@/vendor/InvoicePortalInvoicePage";
import InvoiceFormThanksPage from "@/vendor/InvoiceFormThanksPage";
import VendorFormThanksPage from "@/vendor/VendorFormThanksPage";
import InvoicePortalInvoiceMultiPage from "@/vendor/InvoicePortalInvoiceMultiPage";

export default function App() {
    const [themeMode, setThemeMode] = useState(
        () => localStorage.getItem("themeMode") || "dark",
    );
    const theme = useMemo(
        () => (themeMode == "dark" ? darkTheme : baseTheme),
        [themeMode],
    );

    const toggleTheme = () => {
        setThemeMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
    };

    const queryClient = new QueryClient();

    useEffect(() => {
        localStorage.setItem("themeMode", themeMode);
    }, [themeMode]);

    return (
        <ThemeProvider theme={theme}>
            <QueryClientProvider client={queryClient}>
                <GlobalStyle theme={themeMode} />
                <BrowserRouter>
                    <Auth />
                    <PublicNav />
                    <ScrollToTop />
                    <Api />
                    <Error />
                    <TidioChatController />
                    <Box sx={{ display: "flex", position: "relative" }}>
                        <ProtectedComponents>
                            <TopNav toggleTheme={toggleTheme} />
                            <SideNav />
                            <MobileNav toggleTheme={toggleTheme} />
                            <Popups />
                        </ProtectedComponents>
                        <BaseContainer>
                            <Routes>
                                <Route
                                    path="/dashboard?"
                                    element={
                                        <ProtectedContainer
                                            container={<Dashboard />}
                                        />
                                    }
                                />

                                <Route
                                    path="/join-organization/:inviteToken"
                                    element={<JoinOrganization />}
                                />
                                <Route
                                    path="/sign-up/:inviteToken?"
                                    element={
                                        <SignUp toggleTheme={toggleTheme} />
                                    }
                                />
                                <Route
                                    path="/payment"
                                    element={<PaymentRequired />}
                                />
                                <Route
                                    path="/payment/failed"
                                    element={<PaymentFailed />}
                                />
                                <Route
                                    path="/payment/:purchaseId"
                                    element={<PaymentSuccess />}
                                />
                                <Route path="/verify/*" element={<Verify />} />
                                <Route
                                    path="/initializing"
                                    element={<Init />}
                                />
                                <Route
                                    path="/login/:inviteToken?"
                                    element={<Login />}
                                />
                                <Route
                                    path="/forgot-password"
                                    element={<ForgotPassword />}
                                />
                                <Route
                                    path="/reset-password/:token"
                                    element={<ResetPassword />}
                                />
                                <Route path="/logout" element={<Logout />} />
                                <Route
                                    path="/set-up"
                                    element={
                                        <ProtectedContainer
                                            container={<SetUp />}
                                        />
                                    }
                                />
                                <Route
                                    path="/account"
                                    element={
                                        <ProtectedContainer
                                            container={<Account />}
                                        />
                                    }
                                />

                                <Route
                                    path="/talent"
                                    element={<PersonProfiles />}
                                />
                                <Route
                                    path="/profile/:profileHandle?"
                                    element={<PersonProfile />}
                                />
                                <Route
                                    path="/company-profile/:profileHandle?"
                                    element={<OrgProfile />}
                                />
                                <Route
                                    path="/project-profile/:profileHandle?"
                                    element={<ProjectProfile />}
                                />
                                <Route
                                    path="/projects/:projectId/:organizationId?"
                                    element={
                                        <ProtectedContainer
                                            container={<Project />}
                                            permission={PERMISSION_GET_PROJECTS}
                                        />
                                    }
                                />
                                <Route
                                    path="/projects"
                                    element={
                                        <ProtectedContainer
                                            container={<Projects root />}
                                            permission={PERMISSION_GET_PROJECTS}
                                        />
                                    }
                                />
                                <Route
                                    path="/canvas/:canvasId"
                                    element={
                                        <ProtectedContainer
                                            container={<CanvasPage />}
                                            permission={PERMISSION_GET_PROJECTS}
                                        />
                                    }
                                />
                                <Route
                                    path="/bids/:BID_ID/:organizationId?"
                                    element={
                                        <ProtectedContainer
                                            container={<BidSheet />}
                                            permission={PERMISSION_GET_BIDS}
                                            feature={FEATURE_BIDS}
                                        />
                                    }
                                />
                                <Route
                                    path="/bids/public/:BID_ID/"
                                    element={
                                        <ProtectedContainer
                                            container={<BidSheet isPublic />}
                                            permission={PERMISSION_GET_BIDS}
                                            feature={FEATURE_BIDS}
                                        />
                                    }
                                />
                                <Route
                                    path="/projects/:projectId/actuals"
                                    element={
                                        <ProtectedContainer
                                            container={<Actuals />}
                                            permission={PERMISSION_GET_BIDS}
                                            feature={FEATURE_BIDS}
                                        />
                                    }
                                />
                                <Route
                                    path="/bids"
                                    element={
                                        <ProtectedContainer
                                            container={<Bids />}
                                            permission={PERMISSION_POST_BIDS}
                                            feature={FEATURE_BIDS}
                                        />
                                    }
                                />
                                <Route
                                    path="/invoices"
                                    element={
                                        <ProtectedContainer
                                            container={<Invoices />}
                                            permission={PERMISSION_GET_INVOICES}
                                            feature={FEATURE_INVOICES}
                                        />
                                    }
                                />
                                <Route
                                    path="/bills"
                                    element={
                                        <ProtectedContainer
                                            container={<Bills />}
                                            permission={PERMISSION_GET_BILLS}
                                            feature={FEATURE_BIDS}
                                        />
                                    }
                                />
                                <Route
                                    path="/team"
                                    element={
                                        <ProtectedContainer
                                            container={<Persons />}
                                            permission={PERMISSION_EDIT_PERSONS}
                                            feature={FEATURE_MULTI_PERSON}
                                        />
                                    }
                                />
                                <Route
                                    path="/departments/:clientId"
                                    element={
                                        <ProtectedContainer
                                            container={<Client />}
                                            permission={PERMISSION_GET_CLIENTS}
                                        />
                                    }
                                />
                                <Route
                                    path="/departments"
                                    element={
                                        <ProtectedContainer
                                            container={<Clients />}
                                            permission={PERMISSION_EDIT_CLIENTS}
                                        />
                                    }
                                />
                                <Route
                                    path="/bid-templates"
                                    element={
                                        <ProtectedContainer
                                            container={<TemplateBids />}
                                            permission={PERMISSION_POST_BIDS}
                                            feature={FEATURE_BIDS}
                                        />
                                    }
                                />
                                <Route
                                    path="/reports"
                                    element={
                                        <ProtectedContainer
                                            container={<Reports />}
                                            permission={PERMISSION_GET_PROJECTS}
                                        />
                                    }
                                />

                                {/* Vendor Portal Routes */}
                                <Route path="/ap" element={<InvoicePortal />} />
                                <Route
                                    path="/ap/invoice"
                                    element={<InvoicePortalInvoiceMultiPage />}
                                />
                                <Route
                                    path="/ap/invoice-thank-you"
                                    element={<InvoiceFormThanksPage />}
                                />
                                <Route
                                    path="/ap/vendor-thank-you"
                                    element={<VendorFormThanksPage />}
                                />
                                <Route
                                    path="/ap/vendor"
                                    element={<InvoicePortalVendorPage />}
                                />
                                <Route
                                    path="/vendors/:vendorId?"
                                    element={
                                        <ProtectedContainer
                                            container={<Vendors />}
                                            permission={PERMISSION_GET_PROJECTS}
                                        />
                                    }
                                />
                                <Route
                                    path="/accounting-bills"
                                    element={
                                        <ProtectedContainer
                                            container={<AccountingBills />}
                                            permission={PERMISSION_EDIT_CLIENTS}
                                        />
                                    }
                                />

                                <Route
                                    path="/admin/:tab?"
                                    element={
                                        <ProtectedContainer
                                            container={<Admin />}
                                            permission={
                                                PERMISSION_VIEW_ADMIN_PANEL
                                            }
                                        />
                                    }
                                />
                                <Route
                                    path="/super-admin/:tab?"
                                    element={
                                        <ProtectedContainer
                                            container={<SuperAdmin />}
                                            requireSuperAdmin
                                        />
                                    }
                                />

                                <Route
                                    path="/terms-of-use"
                                    element={
                                        <PublicTerm name={"Terms of Use"} />
                                    }
                                />
                                <Route
                                    path="/privacy-policy"
                                    element={
                                        <PublicTerm name={"Privacy Policy"} />
                                    }
                                />
                                <Route
                                    path="/thank-you-for-signing"
                                    element={
                                        <Confirmation
                                            description={
                                                "Thank you for providing your signature. A copy of the signed document has been sent to your inbox for your records. Should you have any questions, please contact your producer directly."
                                            }
                                        />
                                    }
                                />
                                <Route path="*" element={<_404 />} />
                            </Routes>
                        </BaseContainer>

                        <Footer />
                    </Box>
                </BrowserRouter>
            </QueryClientProvider>
        </ThemeProvider>
    );
}
